import { useState, useRef, useEffect } from "react";
import {
  Star,
  Printer,
  Stethoscope,
  Pill,
  CircleChevronLeft,
  CircleChevronRight,
  Loader2,
  Calendar,
  Clock,
  ChevronDown,
} from "lucide-react";
import doctorAvatar from "../../assets/images/default_avatar.jpg";
import { PatientService } from "../../services/api/patient.service";
import PrescriptionTemplateModal from "../doctors/PrescriptionTemplateModal";
import PatientVitalsSection from "./PatientVitals";

export function PatientHistory(patient: any) {
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const scrollContainerRef = useRef(null);
  
  // Data states
  const [groupedAppointments, setGroupedAppointments] = useState({});
  const [patientDetails, setPatientDetails] = useState({});
  
  // Current selected appointment data
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentDetails, setAppointmentDetails] = useState({});
  const [doctorDetails, setDoctorDetails] = useState({});
  const [patientVitals, setPatientVitals] = useState({});
  const [patientMedications, setPatientMedications] = useState([]);
  const [patientDiagnostics, setPatientDiagnostics] = useState([]);
  
  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchAppointmentHistory = async () => {
      try {
        setLoading(true);
        const response = await PatientService.getPatientHistoryForAppointment(
          patient.patient?.userId || patient.patient?.patientId
        );
        console.log("PatientHistory", response);
        const fullHistory = response.data.patientAppointmentHistory;
        const completedAppointments = fullHistory.filter(
          (appointment) => appointment.appointmentDetails?.status === "Completed"
        );

        // Process appointments
        const grouped = groupAppointmentsByDate(completedAppointments);
        setGroupedAppointments(grouped);
        setPatientDetails(response.data.patientDetails);

        // Set first appointment as default if appointments exist
        if (completedAppointments.length > 0) {
          const firstAppointment = completedAppointments[0];
          handleAppointmentSelect(firstAppointment);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchAppointmentHistory();
  }, [patient]);

  const groupAppointmentsByDate = (appointments) => {
    const grouped = {};
    
    appointments.forEach(appointment => {
      const date = appointment.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      // Add a unique identifier to each appointment for selection tracking
      appointment.id = `${appointment.date}-${appointment.startTime}-${appointment.appointmentDetails?.id || Math.random().toString(36).substr(2, 9)}`;
      grouped[date].push(appointment);
    });
    
    // Sort appointments within each date by time
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => {
        return a.startTime.localeCompare(b.startTime);
      });
    });
    
    return grouped;
  };

  const formatDate = (date) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(date).toLocaleDateString("en-US", options);
  };

  const isToday = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const handleAppointmentSelect = (appointment) => {
    setSelectedAppointment(appointment);
    setSelectedAppointmentId(appointment.id);
    setAppointmentDetails(appointment.appointmentDetails || {});
    setDoctorDetails(appointment.doctorDetails || {});
    setPatientVitals(appointment.patientVitals || {});
    setPatientMedications(appointment.patientMedications || []);
    setPatientDiagnostics(appointment.patientDiagnostics || []);
    setIsDropdownOpen(false);
  };

  const handlePrescriptionPreview = () => {
    console.log("Setting showPreview to true");
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  if (loading) {
    return (
      <div className="w-full h-[80vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
          <p className="text-gray-600 font-medium">
            Loading patient details...
          </p>
        </div>
      </div>
    );
  }

  const hasAppointments = Object.keys(groupedAppointments).length > 0;

  return (
    <div className="flex items-start justify-center gap-2">
      {hasAppointments ? (
        <>
          <div className="w-[55%]">
            {/* Appointment dropdown */}
            <div className="w-full relative">
              <div 
                className="flex items-center justify-between px-4 py-2 rounded-lg bg-gray-100 text-sm font-semibold text-gray-800 hover:bg-gray-200 cursor-pointer border border-gray-200"
                onClick={toggleDropdown}
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  {selectedAppointment ? (
                    <div className="flex flex-col">
                      <span>
                        {isToday(selectedAppointment.date) ? "Today" : formatDate(selectedAppointment.date)}
                      </span>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {selectedAppointment.startTime}
                      </span>
                    </div>
                  ) : (
                    <span>Select appointment date</span>
                  )}
                </div>
                <ChevronDown className="w-4 h-4 text-gray-600" />
              </div>
              
              {/* Dropdown menu */}
              {isDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
                  {Object.keys(groupedAppointments)
                    .sort((a, b) => new Date(b) - new Date(a))
                    .map((date) => (
                      <div key={date} className="border-b last:border-b-0">
                        {/* Date header */}
                        <div className="px-3 py-2 bg-gray-50 font-medium text-sm text-gray-800">
                          {isToday(date) ? "Today" : formatDate(date)}
                          <span className="text-xs text-gray-500 ml-2">
                            ({groupedAppointments[date].length} {groupedAppointments[date].length === 1 ? 'appointment' : 'appointments'})
                          </span>
                        </div>
                        
                        {/* Time slots for this date */}
                        {groupedAppointments[date].map((appointment) => (
                          <div
                            key={appointment.id}
                            className={`flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer ${
                              selectedAppointmentId === appointment.id ? 'bg-blue-100' : ''
                            }`}
                            onClick={() => handleAppointmentSelect(appointment)}
                          >
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-white shadow-sm mr-2">
                              <img
                                src={appointment.doctorDetails?.profileImage || doctorAvatar}
                                alt="doctor"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium">
                                {appointment.startTime} - Dr. {appointment.doctorDetails?.name?.split(' ')[0] || "Unknown"}
                              </p>
                              <p className="text-xs text-gray-600 truncate">
                                {appointment.doctorDetails?.specialization || "Specialist"}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Treated by doctor */}
            <div className="my-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Treated Doctor</h3>
              {selectedAppointment ? (
                <div className="flex items-center gap-4 bg-blue-100 border border-blue-200 p-4 rounded-lg w-full max-w-md">
                  <div className="w-16 h-16 rounded-md overflow-hidden bg-white shadow-sm">
                    <img
                      src={doctorDetails.profileImage || doctorAvatar}
                      alt="doctor"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex flex-col gap-1 overflow-hidden w-full">
                    <p className="text-base font-semibold text-gray-800 truncate">
                      {doctorDetails.name}
                    </p>
                    <p className="text-sm text-gray-600 font-medium truncate">
                      {doctorDetails.education} {doctorDetails.specialization} | {doctorDetails.hospitalName}
                    </p>
                    <p className="text-sm text-emerald-600 font-semibold">
                      {selectedAppointment.startTime}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="py-2">
                  <p className="text-sm font-medium text-gray-500">No data available</p>
                </div>
              )}
            </div>

            {/* Patient Concerns */}
            <div className="my-6">
              <h3 className="font-medium text-base text-gray-800 mb-2">Patient Concerns</h3>
              <div className="w-full min-h-[80px] p-3 rounded-lg border border-gray-200 bg-gray-50">
                {selectedAppointment ? (
                  appointmentDetails?.patient_concern ? (
                    <p className="text-sm text-blue-900">{appointmentDetails.patient_concern}</p>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No concerns provided</p>
                  )
                ) : (
                  <p className="text-sm text-gray-400 italic">No data available</p>
                )}
              </div>
            </div>

            {/* Doctor recommendation */}
            <div className="w-full h-[475px] border border-gray-300 rounded-lg overflow-hidden shadow-sm bg-white">
              {/* heading and print button */}
              <div className="h-[50px] rounded-t-md bg-white w-full p-2 flex items-center justify-between px-2">
                <h3 className="text-sm font-medium">Doctor Recommendations</h3>
                {selectedAppointment && (
                  <button
                    className="hover:bg-[#5c173f] transition-all text-white bg-[#771D51] py-1 px-4 rounded-full flex items-center justify-center gap-1 text-sm"
                    onClick={handlePrescriptionPreview}
                  >
                    Print <Printer className="w-4 h-4" />
                  </button>
                )}
              </div>

              <PrescriptionTemplateModal
                showPreview={showPreview}
                onClose={handleClosePreview}
                doctorDetails={doctorDetails}
                patientDetails={patientDetails}
                appointment={appointmentDetails}
                vitals={patientVitals}
                patientDiagnostics={patientDiagnostics}
                patientMedications={patientMedications}
                patientAppointmentHistory={selectedAppointment}
              />

              {/* Two parts of recommendation parent box */}
              <div className="bg-white flex items-center justify-between w-full h-[calc(100%-50px)] rounded-b-md overflow-hidden">
                {/* Diagnosis Section */}
                <div
                  className="rounded-l-md rounded-t-none overflow-hidden w-[49.5%] h-full p-4"
                  style={{ backgroundColor: "#EEF2F5" }}
                >
                  <h4 className="flex items-center justify-start gap-2 text-sm font-medium">
                    <Stethoscope className="w-4 h-4" /> Diagnosis
                  </h4>
                  <ul className="hiddenScrollbar mt-2 w-full h-[calc(100%-15px)] overflow-hidden overflow-y-auto space-y-2">
                    {patientDiagnostics.length > 0 ? (
                      patientDiagnostics.map((diagnostic: any, index: number) => (
                        <li
                          key={index}
                          className="bg-white px-4 py-2 rounded-md shadow-sm border font-medium text-[0.85em] border-gray-200"
                        >
                          {index + 1}. {diagnostic.diagnostic_name}
                        </li>
                      ))
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-gray-500 text-sm">No data available</p>
                      </div>
                    )}
                  </ul>
                </div>

                {/* Medications Section */}
                <div
                  className="rounded-r-md rounded-t-none overflow-hidden w-[49.5%] h-full p-4"
                  style={{ backgroundColor: "#EEF2F5" }}
                >
                  <h4 className="flex items-center justify-start gap-2 text-sm font-medium">
                    <Pill className="w-4 h-4" /> Medications
                  </h4>
                  <ul className="hiddenScrollbar mt-2 w-full h-[calc(100%-15px)] overflow-hidden overflow-y-auto space-y-2">
                    {patientMedications.length > 0 ? (
                      patientMedications.map((med: any, index: number) => (
                        <li
                          key={index}
                          className="bg-white px-4 py-2 rounded-md shadow-sm border border-gray-200"
                        >
                          <p className="font-medium text-[0.85em]">
                            {index + 1}. {med.medicine_name}
                          </p>
                          <p className="text-xs text-gray-600">
                            {med.duration_days}
                            {" Days "}
                            {med.frequency_morning ? `Morning ` : ""}
                            {med.frequency_noon ? `Noon ` : ""}
                            {med.frequency_night ? `Night ` : ""}
                            {","}
                            {med.before_food ? "Before Food" : "After Food"}
                          </p>
                        </li>
                      ))
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-gray-500 text-sm">No data available</p>
                      </div>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Patient vitals */}
          <PatientVitalsSection patientVitals={patientVitals} />
        </>
      ) : (
        <div className="flex flex-col w-full items-center justify-center py-16 px-4 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-full bg-[#771D51]/10 flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-[#771D51]" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No completed appointments available
            </h3>
            <p className="text-gray-500 text-sm mb-6">
              You don't have any completed appointments.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}